import React from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import qs from 'qs';
import Skeleton from '@material-ui/lab/Skeleton';
import HomeHeroCarousel from 'components/HomeHeroCarousel';
import HomeHeroReferral from 'components/HomeHeroCarousel/components/HomeHeroReferral';
import { getClientLandingImagesGQl } from 'containers/HomePage/utils';
import { makeSelectProfileFilters } from 'containers/Auth/selectors';
import {
  makeSelectClientLanding,
  makeSelectClientLandingProcessing,
  makeSelectPageLanding,
  makeSelectPageLandingProcessing,
} from 'containers/ClientLanding/selectors';
import {
  makeSelectClientDetails,
  makeSelectReferrals,
} from 'containers/Main/selectors';
import { createStructuredSelector } from 'reselect';
import { withStyles } from '@material-ui/core/styles';
import { isLocalStorageAllowed } from 'utils/stringUtils';
// import Cookie from 'js-cookie';
import { Experiment, Variant, emitter } from '@marvelapp/react-ab-test';
import Mixpanel from 'utils/mixpanelService';
import { Divider } from '@material-ui/core';
import useContentfulLocale from 'components/useContentfulLocale';
import GqlClient from 'utils/graphqlService';
import { useQuery } from 'react-query';
import withSizes from 'react-sizes';
import GuideMe from './components/GuideMeB';
import VariantA from './variants/A';
import VariantB from './variants/B';
import VariantC from './variants/C';
import VariantD from './variants/D';
import VariantE from './variants/E';
import VariantG from './variants/G';
import VariantF from './variants/F';
import VariantI from './variants/I';
import VariantH from './variants/H';
import VariantJ from './variants/J';
import VariantK from './variants/K';
import VariantL from './variants/L';
import PageContext from './PageContext';
import { filterLandingFeaturedResources } from './utils';
import createClientLandingQuery from '../../containers/ClientLanding/services/clientLandingInfo.gql';

const stateSelector = createStructuredSelector({
  clientDetails: makeSelectClientDetails(),
  clientLandingInfo: makeSelectClientLanding(),
  clientLandingFetching: makeSelectClientLandingProcessing(),
  pageLandingInfo: makeSelectPageLanding(),
  pageLandingFetching: makeSelectPageLandingProcessing(),
  profileFilters: makeSelectProfileFilters(),
  referrals: makeSelectReferrals(),
});

const secondaryLanguageQueryFn = async (
  _,
  { location, landingInfo, localeFilters },
) => {
  const queryParams = qs.parse(location.search.slice(1));
  const isPreviewMode = _get(queryParams, 'mode') === 'preview';
  const item = await GqlClient.query({
    query: createClientLandingQuery(isPreviewMode),
    variables: {
      slug: landingInfo.slug,
      locale: _get(localeFilters, 'locale').includes('US') ? 'es' : 'en-US',
    },
  });
  return item;
};

function HeroLanding({ isLanding, isMobile, isIPad }) {
  const {
    clientDetails,
    clientLandingInfo,
    clientLandingFetching,
    pageLandingInfo,
    pageLandingFetching,
    profileFilters,
    referrals,
  } = useSelector(stateSelector);
  const localeFilters = useContentfulLocale();
  const location = useLocation();

  const finalData = isLanding ? pageLandingInfo : clientLandingInfo;
  const variantsWithSecondaryLanguage = ['J-OneCard', 'K-NoCard', 'L-4Card'];

  const { data } = useQuery(
    !_isEmpty(finalData) &&
      variantsWithSecondaryLanguage.includes(finalData.heroStyle) && [
        'hero-secondary-landing',
        {
          location,
          localeFilters,
          landingInfo: finalData,
        },
      ],
    secondaryLanguageQueryFn,
  );

  const finalProcessing = isLanding
    ? pageLandingFetching
    : clientLandingFetching;

  if (finalProcessing) return <Skeleton variant="rect" height="500px" />;
  if (_isEmpty(finalData)) return null;

  const { primaryColor, siteImagesCollection, heroStyle, pageText } = finalData;
  const buttonJson = _get(pageText, 'button');
  const hasButtonJson =
    !_isEmpty(buttonJson) && _get(buttonJson, 'label', false);
  if (_isEmpty(heroStyle) || heroStyle === undefined) {
    // emit winner only if hero style is not set in contentful
    emitter.calculateActiveVariant('hero-variant');
  }
  const shouldStaticBGImage = heroStyle === 'I-ButtonCard' && !hasButtonJson;
  const siteImages = _get(siteImagesCollection, 'items', []);
  const hasSiteImages = !_isEmpty(siteImages);
  const landingImages = getClientLandingImagesGQl(
    siteImages,
    shouldStaticBGImage,
  );

  const finalDataInOtherLang = _get(data, 'data.landingCollection.items[0]');
  const {
    fontColor,
    light,
    title,
    isTransparent,
    ...backgroundImages
  } = landingImages;
  const activeVariant = emitter.getActiveVariant('hero-variant');
  const isVariantC = heroStyle === 'C-SixCard' || activeVariant === 'variantC';
  const showCollage =
    isVariantC &&
    !_isEmpty(_get(finalData, 'featuredCollageCollection.items', []));

  const { insuranceTags = [], state } = profileFilters;
  const landingResources = showCollage
    ? _get(finalData, 'featuredCollageCollection.items', [])
    : _get(finalData, 'featuredResourcesCollection.items', []);
  const landingResourcesInOtherLang = showCollage
    ? _get(finalDataInOtherLang, 'featuredCollageCollection.items', [])
    : _get(finalDataInOtherLang, 'featuredResourcesCollection.items', []);
  const featuredResources = [
    filterLandingFeaturedResources(landingResources, insuranceTags, state),
    filterLandingFeaturedResources(
      landingResourcesInOtherLang,
      insuranceTags,
      state,
    ),
  ];

  // referralClient is the client that is being referred to by the referral
  // and it can be a client or a client group
  // so we've to check if the logged in client short name or its group short name
  // is in the referral client short name
  const hasClientAccessToReferral = referralClient =>
    !_isEmpty(referralClient) &&
    [
      clientDetails.shortName,
      _get(clientDetails, 'clientGroup.shortName'),
    ].includes(_get(referralClient, 'shortName'));

  const homeHeroReferrals = referrals.filter(item => {
    if (item.style !== 'Home Hero') return false;
    if (
      Date.now() > _get(item, 'context.validUntil', Number.MAX_VALUE) ||
      _isEmpty(clientDetails)
    )
      return false;

    if (_isEmpty(_get(item, 'clientCollection.items', []))) {
      return !_get(item, 'excludeClientCollection.items', []).some(
        hasClientAccessToReferral,
      );
    }

    return _get(item, 'clientCollection.items', []).some(
      hasClientAccessToReferral,
    );
  });
  const hasHomeHeroReferrals = homeHeroReferrals.length > 0;
  const shouldUseCarousel = hasHomeHeroReferrals && !isLanding;

  const styles = theme => ({
    homeWrapper: {
      display: 'flex',
      justifyContent: 'center',
      position: 'relative',
      background:
        isVariantC || !hasSiteImages
          ? `#${primaryColor}`
          : `${isTransparent && primaryColor ? `#${primaryColor}` : ''} url(${
              backgroundImages.large
            })  no-repeat center`,
      backgroundSize: 'cover',
      paddingBottom: shouldUseCarousel ? 24 : 0,
      [theme.breakpoints.down('1024')]: {
        background:
          isVariantC || !hasSiteImages
            ? `#${primaryColor}`
            : `${isTransparent && primaryColor ? `#${primaryColor}` : ''} url(${
                backgroundImages.medium
              })  no-repeat center`,
        backgroundSize: 'cover',
      },
      minHeight: 400,
      [theme.breakpoints.down('600')]: {
        background:
          isVariantC || !hasSiteImages
            ? `#${primaryColor}`
            : `${isTransparent && primaryColor ? `#${primaryColor}` : ''} url(${
                backgroundImages.small
              })  no-repeat center`,
        backgroundSize: 'cover',
        paddingBottom: 0,
        paddingTop: shouldUseCarousel ? 16 : 0,
        minHeight: 'auto',
      },
    },
    container: {
      display: !isMobile && !isIPad && 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 1140,
      '& .MuiDivider-middle': {
        backgroundColor: '#FFF',
        marginLeft: '20px',
        marginRight: '20px',
        height: '70%',
      },
    },
  });
  const div = props => (
    <div className={props.classes.homeWrapper} data-test-id="hero-landing">
      <div className={props.classes.container}>{props.children}</div>
    </div>
  );
  const Styled = withStyles(styles)(div);
  const getVariants = () => {
    const clientVariants = {
      'A-FourCard': {
        comp: (
          <>
            <VariantA />
          </>
        ),
        name: 'variantA',
      },
      'B-FourCard': {
        comp: (
          <>
            <VariantB />
            <GuideMe />
          </>
        ),
        name: 'variantB',
      },
      'C-SixCard': {
        comp: (
          <>
            <VariantC />
            <GuideMe />
          </>
        ),
        name: 'variantC',
      },
      'D-EightCard': {
        comp: (
          <>
            <VariantD />
            <GuideMe />
          </>
        ),
        name: 'variantD',
      },
      'E-Search': {
        comp: <VariantE />,
        name: 'variantE',
      },
      'F-OneCard': {
        comp: <VariantF shouldUseCarousel />,
        name: 'variantF',
      },
      'G-NoCard': {
        comp: <VariantG />,
        name: 'variantG',
      },
      'H-VideoCard': {
        comp: <VariantH />,
        name: 'variantG',
      },
      'I-ButtonCard': {
        comp: <VariantI />,
        name: 'variantI',
      },
      'J-OneCard': {
        comp: <VariantJ isMobile={isMobile} isIPad={isIPad} />,
        name: 'variantG',
      },
      'K-NoCard': {
        comp: (
          <>
            <VariantK />
            {!isMobile && <Divider orientation="vertical" variant="middle" />}
            <VariantK otherLanguage />
          </>
        ),
        name: 'variantG',
      },
      'L-4Card': {
        comp: (
          <>
            {isMobile ? (
              <VariantL isMobile />
            ) : (
              <>
                <VariantL />
                <Divider orientation="vertical" variant="middle" />
                <VariantL otherLanguage />
              </>
            )}
          </>
        ),
        name: 'variantG',
      },
    };

    const { comp } = clientVariants[heroStyle] || clientVariants['A-FourCard'];
    if (isLocalStorageAllowed()) {
      localStorage.removeItem('PUSHTELL-hero-variant');
    }
    return comp;
  };

  const sendMixpanelEvent = (eventTitle, eventProps) => {
    const finalProps = {
      ...eventProps,
      section: 'hero',
    };
    Mixpanel.track(eventTitle, finalProps);
  };

  const Wrapper = shouldUseCarousel ? HomeHeroCarousel : React.Fragment;
  const wrapperProps = {};
  if (shouldUseCarousel) {
    wrapperProps.timer = _get(
      clientDetails,
      'context.promotionalHomeHeroTimer',
    );
  }

  return (
    <PageContext.Provider
      value={{
        clientInfo: [
          finalData,
          {
            ...finalDataInOtherLang,
            locale: _get(localeFilters, 'locale').includes('US')
              ? 'es'
              : 'en-US',
          },
        ],
        textColor: fontColor ? `#${fontColor}` : '',
        sendMixpanelEvent,
        heroStyle,
        landingInfo: clientLandingInfo,
        featuredResources,
      }}
    >
      <Wrapper {...wrapperProps}>
        <Styled>
          {heroStyle ? (
            getVariants()
          ) : (
            <Experiment name="hero-variant">
              <Variant name="variantA">
                <VariantA />
              </Variant>
              <Variant name="variantB">
                <VariantB />
                <GuideMe />
              </Variant>
              <Variant name="variantC">
                <VariantC />
                <GuideMe />
              </Variant>
              <Variant name="variantD">
                <VariantD />
                <GuideMe />
              </Variant>
            </Experiment>
          )}
        </Styled>
        {shouldUseCarousel &&
          homeHeroReferrals.map(item => (
            <HomeHeroReferral key={item.slug} item={item} />
          ))}
      </Wrapper>
    </PageContext.Provider>
  );
}

HeroLanding.propTypes = {
  isLanding: PropTypes.bool,
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width <= 480,
  isIPad: width <= 1023 && width >= 480,
});

export default withSizes(mapSizesToProps)(React.memo(HeroLanding));
